import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { getDashboardStats, getDocuments } from "../lib/api";
import { ScoreRing, StatusBadge } from "../components/SharedComponents";

const RANGES = [
  { key:"1d",  label:"Today" },
  { key:"1w",  label:"Week" },
  { key:"1m",  label:"Month" },
  { key:"3m",  label:"3 Months" },
  { key:"all", label:"All Time" },
];

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:"var(--white)", border:"1px solid var(--border)", borderRadius:6, padding:"8px 12px", fontSize:12, fontFamily:"var(--font-mono)", boxShadow:"var(--shadow-md)" }}>
      <div style={{ color:"var(--text-secondary)", marginBottom:5 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color:p.color }}>{p.name}: <strong>{p.value}</strong></div>
      ))}
    </div>
  );
};

/* ── Custom YAxis tick that wraps long labels ── */
const wrapText = (text, maxChars) => {
  const words = (text || "").split(" ");
  const lines = [];
  let cur = "";
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (test.length > maxChars && cur) { lines.push(cur); cur = w; }
    else { cur = test; }
  }
  if (cur) lines.push(cur);
  return lines;
};

const WrappedYAxisTick = ({ x, y, payload, width }) => {
  const fontSize = 11;
  const lineHeight = 14;
  const maxChars = Math.floor(((width || 180) - 12) / 6.6);
  const lines = wrapText(payload.value, maxChars);
  const blockTop = y - (lines.length * lineHeight) / 2 + fontSize * 0.35;
  return (
    <g>
      {lines.map((line, i) => (
        <text key={i} x={x - 6} y={blockTop + i * lineHeight} textAnchor="end"
          fill="var(--text-secondary)" fontSize={fontSize} fontFamily="var(--font-mono)">{line}</text>
      ))}
    </g>
  );
};

function RangeSelector({ value, onChange }) {
  return (
    <div style={{ display:"flex", background:"var(--gray-100)", borderRadius:6, padding:2, gap:1 }}>
      {RANGES.map(r => (
        <button key={r.key} onClick={() => onChange(r.key)} style={{
          padding:"3px 10px", borderRadius:4, fontSize:11, fontFamily:"var(--font-mono)",
          fontWeight:500, cursor:"pointer", border:"none", transition:"all 100ms ease",
          background: value === r.key ? "var(--white)" : "transparent",
          color: value === r.key ? "var(--text-primary)" : "var(--text-tertiary)",
          boxShadow: value === r.key ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
        }}>{r.label}</button>
      ))}
    </div>
  );
}

function subtitleForRange(range) {
  const now = new Date();
  const fmtNo = d => d.toLocaleDateString("en-US", { month:"short", day:"numeric" });
  if (range === "1d") return `Today · ${now.toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" })}`;
  if (range === "1w") { const s = new Date(now); s.setDate(now.getDate() - 6); return `${fmtNo(s)} – ${fmtNo(now)}, ${now.getFullYear()}`; }
  if (range === "1m") { const s = new Date(now); s.setMonth(now.getMonth() - 1); return `${fmtNo(s)} – ${fmtNo(now)}, ${now.getFullYear()}`; }
  if (range === "3m") { const s = new Date(now); s.setMonth(now.getMonth() - 3); return `${fmtNo(s)} – ${fmtNo(now)}, ${now.getFullYear()}`; }
  return "All time";
}

const isSourceFile = d => d.sourceType && d.sourceType !== "Combined" && d.sourceType !== "PID_QA";

const TrendDelta = ({ delta, suffix }) => {
  if (delta == null) return null;
  const color = delta > 0 ? "var(--success)" : delta < 0 ? "var(--error)" : "var(--text-tertiary)";
  const arrow = delta > 0 ? "↑" : delta < 0 ? "↓" : "—";
  return <span style={{ fontSize:11, fontFamily:"var(--font-mono)", color }}>{arrow} {Math.abs(delta)} {suffix}</span>;
};

export default function Dashboard() {
  const [data, setData]             = useState(null);
  const [recentDocs, setRecentDocs] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [range, setRange]           = useState("1w");
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      getDashboardStats(range),
      getDocuments().catch(() => [])
    ]).then(([statsData, docs]) => {
      setData(statsData);
      setRecentDocs((docs || []).filter(d => !isSourceFile(d)).slice(0, 5));
    }).finally(() => setLoading(false));
  }, [range]);

  if (loading) return (
    <div className="page-body">
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
        {[0,1,2,3].map(i => (
          <div key={i} className="card" style={{ padding:20 }}>
            <div className="skeleton" style={{ height:36, width:36, borderRadius:8, marginBottom:12 }}/>
            <div className="skeleton" style={{ height:10, width:80, marginBottom:10 }}/>
            <div className="skeleton" style={{ height:28, width:60 }}/>
          </div>
        ))}
      </div>
    </div>
  );

  const { stats = {}, trend = [], topFailedChecks = [], turnaroundTrend = [] } = data || {};
  const s = {
    totalDocuments: stats.totalDocuments ?? 0,
    passRate:       stats.passRate       ?? 0,
    failedThisWeek: stats.failedThisWeek ?? 0,
    avgScore:       stats.avgScore       ?? 0,
    totalTrend:     stats.totalTrend     ?? 0,
    passRateTrend:  stats.passRateTrend  ?? 0,
    scoreTrend:     stats.scoreTrend     ?? 0,
    overrideRate:   stats.overrideRate   ?? 0,
    overridePass:   stats.overridePass   ?? 0,
    overrideTotal:  stats.overrideTotal  ?? 0,
    overrideFail:   stats.overrideFail   ?? 0,
    confirmedFail:  stats.confirmedFail  ?? 0,
    confirmedPass:  stats.confirmedPass  ?? 0,
    aiAccuracy:     stats.aiAccuracy     ?? 100,
    aiCorrect:      stats.aiCorrect      ?? 0,
    aiWrong:        stats.aiWrong        ?? 0,
    aiHealth:       stats.aiHealth       ?? "healthy",
    worstChecks:    stats.worstChecks    ?? [],
    pendingReview:  stats.pendingReview  ?? 0,
    reviewedCount:  stats.reviewedCount  ?? 0,
  };

  const passedCases = s.totalDocuments - s.failedThisWeek;
  const alertCases = recentDocs.filter(d => d.alertStatus === "ALERT" || d.alertStatus === "MANUAL_ALERT").length;

  const YAXIS_WIDTH = 200;
  const maxChars = Math.floor((YAXIS_WIDTH - 12) / 6.6);
  const ROW_BASE = 36, ROW_EXTRA = 14;
  const failedChecksHeight = topFailedChecks.length === 0 ? 190 : Math.max(190,
    topFailedChecks.reduce((t, c) => t + ROW_BASE + Math.max(0, wrapText(c.name, maxChars).length - 1) * ROW_EXTRA, 0));

  return (
    <>
      <div className="page-header">
        <div className="page-title-group">
          <div className="page-title">Dashboard</div>
          <div className="page-subtitle">{subtitleForRange(range)}</div>
        </div>
        <div className="header-actions">
          <RangeSelector value={range} onChange={r => { setRange(r); setLoading(true); }}/>
          <button className="btn btn-primary btn-sm" onClick={() => navigate("/upload")}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" style={{ width:14, height:14 }}>
              <line x1="8" y1="3" x2="8" y2="13"/><line x1="3" y1="8" x2="13" y2="8"/>
            </svg>Upload
          </button>
        </div>
      </div>

      <div className="page-body">

        {/* ── 4 Stat Cards ──────────────────────────────────── */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:12, marginBottom:16 }}>

          {/* Total Cases */}
          <div className="card" style={{ padding:"18px 20px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
              <div style={{ width:36, height:36, borderRadius:8, background:"#eff6ff", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="#3b82f6" strokeWidth="1.5"><path d="M9 1H4a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V5L9 1z"/><path d="M9 1v4h4"/></svg>
              </div>
              {alertCases > 0 && (
                <span style={{ display:"inline-flex", alignItems:"center", gap:3, padding:"2px 7px", borderRadius:12,
                  background:"#fef2f2", border:"1px solid #fecaca", fontSize:10, fontWeight:600, color:"#dc2626", fontFamily:"var(--font-mono)" }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/></svg>{alertCases}
                </span>
              )}
            </div>
            <div style={{ fontSize:32, fontWeight:300, color:"#3b82f6", lineHeight:1, letterSpacing:"-0.03em" }}>{s.totalDocuments}</div>
            <div style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.06em", marginTop:4 }}>Total Cases</div>
            <div style={{ marginTop:6, fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>{passedCases} passed · {s.failedThisWeek} failed</div>
            <div style={{ marginTop:2 }}><TrendDelta delta={s.totalTrend} suffix="vs prior"/></div>
          </div>

          {/* Pass Rate */}
          <div className="card" style={{ padding:"18px 20px" }}>
            <div style={{ width:36, height:36, borderRadius:8, background: s.passRate >= 70 ? "#f0fdf4" : s.passRate >= 50 ? "#fffbeb" : "#fef2f2",
              display:"flex", alignItems:"center", justifyContent:"center", marginBottom:10 }}>
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke={s.passRate >= 70 ? "#16a34a" : s.passRate >= 50 ? "#d97706" : "#dc2626"} strokeWidth="1.5">
                <circle cx="8" cy="8" r="6"/><polyline points="5 8 7 10 11 6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div style={{ fontSize:32, fontWeight:300, color: s.passRate >= 70 ? "#16a34a" : s.passRate >= 50 ? "#d97706" : "#dc2626", lineHeight:1, letterSpacing:"-0.03em" }}>{s.passRate}%</div>
            <div style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.06em", marginTop:4 }}>Pass Rate</div>
            <div style={{ marginTop:6 }}><TrendDelta delta={s.passRateTrend} suffix="vs prior"/></div>
          </div>

          {/* Avg QA Score */}
          <div className="card" style={{ padding:"18px 20px" }}>
            <div style={{ width:36, height:36, borderRadius:8, background:"#fffbeb", display:"flex", alignItems:"center", justifyContent:"center", marginBottom:10 }}>
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="#d97706" strokeWidth="1.5"><polyline points="2 11 5 7 8 9 11 4 14 6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <div style={{ fontSize:32, fontWeight:300, color:"#d97706", lineHeight:1, letterSpacing:"-0.03em" }}>{s.avgScore}</div>
            <div style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.06em", marginTop:4 }}>Avg QA Score</div>
            <div style={{ marginTop:6 }}><TrendDelta delta={s.scoreTrend} suffix="pts vs prior"/></div>
          </div>

          {/* Review Backlog */}
          <div className="card" style={{ padding:"18px 20px", borderLeft: s.pendingReview > 0 ? "3px solid #dc2626" : "3px solid #16a34a" }}>
            <div style={{ width:36, height:36, borderRadius:8, background: s.pendingReview > 0 ? "#fef2f2" : "#f0fdf4",
              display:"flex", alignItems:"center", justifyContent:"center", marginBottom:10 }}>
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke={s.pendingReview > 0 ? "#dc2626" : "#16a34a"} strokeWidth="1.5">
                <circle cx="8" cy="8" r="6"/><polyline points="8 4 8 8 11 10" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div style={{ fontSize:32, fontWeight:300, color: s.pendingReview > 0 ? "#dc2626" : "#16a34a", lineHeight:1, letterSpacing:"-0.03em" }}>{s.pendingReview}</div>
            <div style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.06em", marginTop:4 }}>Review Backlog</div>
            <div style={{ marginTop:6, fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>
              {s.pendingReview > 0 ? `${s.reviewedCount} of ${s.failedThisWeek} reviewed` : s.failedThisWeek > 0 ? "All failed cases reviewed" : "No failed cases"}
            </div>
            {s.failedThisWeek > 0 && (
              <div style={{ marginTop:6, height:3, background:"var(--gray-100)", borderRadius:2, overflow:"hidden" }}>
                <div style={{ height:"100%", borderRadius:2, transition:"width 0.4s",
                  width:`${(s.reviewedCount / s.failedThisWeek) * 100}%`,
                  background: s.pendingReview === 0 ? "#16a34a" : "#3b82f6" }}/>
              </div>
            )}
          </div>
        </div>

        {/* ── Charts Row 1: Volume + Failed Checks ────────── */}
        <div className="two-col" style={{ marginBottom:14 }}>
          <div className="card">
            <div className="card-header"><div><div className="card-title">Case Volume</div><div className="card-subtitle">Passed vs. failed · {RANGES.find(r => r.key === range)?.label}</div></div></div>
            <div className="card-body" style={{ paddingTop:4 }}>
              <ResponsiveContainer width="100%" height={190}>
                <AreaChart data={trend} margin={{ top:4, right:4, bottom:0, left:-24 }}>
                  <defs>
                    <linearGradient id="gPass" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="var(--success)" stopOpacity={0.2}/><stop offset="95%" stopColor="var(--success)" stopOpacity={0}/></linearGradient>
                    <linearGradient id="gFail" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="var(--error)" stopOpacity={0.15}/><stop offset="95%" stopColor="var(--error)" stopOpacity={0}/></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" vertical={false}/>
                  <XAxis dataKey="date" tick={{ fill:"var(--text-tertiary)", fontSize:10, fontFamily:"var(--font-mono)" }} axisLine={false} tickLine={false}/>
                  <YAxis tick={{ fill:"var(--text-tertiary)", fontSize:10, fontFamily:"var(--font-mono)" }} axisLine={false} tickLine={false} allowDecimals={false}/>
                  <Tooltip content={<ChartTip/>}/>
                  <Area type="monotone" dataKey="passed" stroke="var(--success)" strokeWidth={1.5} fill="url(#gPass)" name="Passed"/>
                  <Area type="monotone" dataKey="failed" stroke="var(--error)" strokeWidth={1.5} fill="url(#gFail)" name="Failed"/>
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><div><div className="card-title">Most Commonly Failed Checks</div><div className="card-subtitle">Top failing QA rules · {RANGES.find(r => r.key === range)?.label}</div></div></div>
            <div className="card-body" style={{ paddingTop:4 }}>
              {topFailedChecks.length === 0 ? (
                <div style={{ height:190, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:8 }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--gray-300)" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/></svg>
                  <span style={{ fontSize:12, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>No failures in this period</span>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={failedChecksHeight}>
                  <BarChart data={topFailedChecks} layout="vertical" margin={{ top:0, right:32, bottom:0, left:8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" horizontal={false}/>
                    <XAxis type="number" allowDecimals={false} tick={{ fill:"var(--text-tertiary)", fontSize:10, fontFamily:"var(--font-mono)" }} axisLine={false} tickLine={false}/>
                    <YAxis type="category" dataKey="name" width={YAXIS_WIDTH} tick={<WrappedYAxisTick width={YAXIS_WIDTH}/>} axisLine={false} tickLine={false}/>
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (<div style={{ background:"var(--white)", border:"1px solid var(--border)", borderRadius:6, padding:"8px 12px", fontSize:12, fontFamily:"var(--font-mono)", boxShadow:"var(--shadow-md)", maxWidth:280 }}>
                        <div style={{ color:"var(--text-secondary)", marginBottom:5, whiteSpace:"normal", lineHeight:1.4 }}>{d.name}</div>
                        <div style={{ color:"var(--error)", fontWeight:600 }}>{d.count} failure{d.count !== 1 ? "s" : ""}</div>
                      </div>);
                    }}/>
                    <Bar dataKey="count" name="Failures" fill="var(--error)" radius={[0,3,3,0]} opacity={0.75}/>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* ── Charts Row 2: Turnaround + Override Rate ─────── */}
        <div className="two-col" style={{ marginBottom:16 }}>
          <div className="card">
            <div className="card-header"><div><div className="card-title">Review Turnaround</div><div className="card-subtitle">Avg hours from upload to review · {RANGES.find(r => r.key === range)?.label}</div></div></div>
            <div className="card-body" style={{ paddingTop:4 }}>
              {turnaroundTrend.length === 0 ? (
                <div style={{ height:190, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:8 }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--gray-300)" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <span style={{ fontSize:12, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>No completed reviews yet</span>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={190}>
                  <LineChart data={turnaroundTrend} margin={{ top:4, right:4, bottom:0, left:-24 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" vertical={false}/>
                    <XAxis dataKey="date" tick={{ fill:"var(--text-tertiary)", fontSize:10, fontFamily:"var(--font-mono)" }} axisLine={false} tickLine={false}/>
                    <YAxis tick={{ fill:"var(--text-tertiary)", fontSize:10, fontFamily:"var(--font-mono)" }} axisLine={false} tickLine={false} unit="h"/>
                    <Tooltip content={<ChartTip/>}/>
                    <Line type="monotone" dataKey="avgHours" stroke="var(--blue-500)" strokeWidth={2} dot={{ r:3, fill:"var(--blue-500)" }} name="Avg Hours"/>
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">AI performance</div>
                <div className="card-subtitle">Reviewer decisions · {RANGES.find(r => r.key === range)?.label}</div>
              </div>
              <span style={{
                fontSize:10, fontFamily:"var(--font-mono)", fontWeight:600, padding:"3px 10px", borderRadius:5,
                background: s.aiHealth === "healthy" ? "#f0fdf4" : s.aiHealth === "attention" ? "#fffbeb" : "#fef2f2",
                color: s.aiHealth === "healthy" ? "#16a34a" : s.aiHealth === "attention" ? "#d97706" : "#dc2626",
                border: `1px solid ${s.aiHealth === "healthy" ? "#bbf7d0" : s.aiHealth === "attention" ? "#fde68a" : "#fecaca"}`
              }}>
                {s.aiHealth === "healthy" ? "Healthy" : s.aiHealth === "attention" ? "Needs attention" : "Review prompts"}
              </span>
            </div>
            <div className="card-body" style={{ paddingTop:8 }}>
              {s.overrideTotal === 0 ? (
                <div style={{ height:190, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column",
                  color:"var(--text-tertiary)", fontFamily:"var(--font-mono)", fontSize:12 }}>
                  <div style={{ marginBottom:4 }}>No reviewer decisions yet</div>
                  <div style={{ fontSize:10 }}>Override or confirm QA checks to see AI accuracy</div>
                </div>
              ) : (
                <>
                  {/* ── Top row: accuracy ring + metric tiles ── */}
                  <div style={{ display:"flex", alignItems:"center", gap:20, marginBottom:14 }}>
                    <div style={{ textAlign:"center", flexShrink:0 }}>
                      <svg width="72" height="72" viewBox="0 0 80 80">
                        <circle cx="40" cy="40" r="34" fill="none" stroke="var(--gray-200, #e5e7eb)" strokeWidth="6"/>
                        <circle cx="40" cy="40" r="34" fill="none"
                          stroke={s.aiAccuracy >= 80 ? "#16a34a" : s.aiAccuracy >= 60 ? "#d97706" : "#dc2626"}
                          strokeWidth="6" strokeLinecap="round"
                          strokeDasharray={`${(s.aiAccuracy / 100) * 213.6} 213.6`}
                          transform="rotate(-90 40 40)"/>
                        <text x="40" y="38" textAnchor="middle" fontSize="18" fontWeight="500"
                          fill="var(--text-primary)">{s.aiAccuracy}%</text>
                        <text x="40" y="50" textAnchor="middle" fontSize="8"
                          fill="var(--text-tertiary)" fontFamily="var(--font-mono)">ACCURACY</text>
                      </svg>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6, flex:1 }}>
                      {[
                        { val: s.aiCorrect, label:"AI correct", color:"#16a34a" },
                        { val: s.aiWrong, label:"AI wrong", color:"#dc2626" },
                        { val: s.overridePass, label:"False positives", color:"#d97706" },
                        { val: s.overrideFail, label:"Missed issues", color:"#7c3aed" },
                      ].map(m => (
                        <div key={m.label} style={{ padding:"6px 10px", background:"var(--gray-50, #f9fafb)", borderRadius:6 }}>
                          <div style={{ fontSize:16, fontWeight:500, color:m.color, fontFamily:"var(--font-mono)" }}>{m.val}</div>
                          <div style={{ fontSize:9.5, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.04em" }}>{m.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ── Decision breakdown bar ── */}
                  <div style={{ marginBottom:12 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>Decision breakdown</span>
                      <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>{s.overrideTotal} total</span>
                    </div>
                    <div style={{ height:7, background:"var(--gray-100, #f3f4f6)", borderRadius:4, overflow:"hidden", display:"flex" }}>
                      {s.confirmedFail > 0 && <div style={{ width:`${(s.confirmedFail/s.overrideTotal)*100}%`, height:"100%", background:"#16a34a" }}/>}
                      {s.confirmedPass > 0 && <div style={{ width:`${(s.confirmedPass/s.overrideTotal)*100}%`, height:"100%", background:"#bbf7d0" }}/>}
                      {s.overridePass > 0 && <div style={{ width:`${(s.overridePass/s.overrideTotal)*100}%`, height:"100%", background:"#fde68a" }}/>}
                      {s.overrideFail > 0 && <div style={{ width:`${(s.overrideFail/s.overrideTotal)*100}%`, height:"100%", background:"#fecaca" }}/>}
                    </div>
                    <div style={{ display:"flex", gap:10, marginTop:5, flexWrap:"wrap" }}>
                      {[
                        { label:"Confirmed fail", val:s.confirmedFail, color:"#16a34a" },
                        { label:"Confirmed pass", val:s.confirmedPass, color:"#bbf7d0", textColor:"#16a34a" },
                        { label:"False positive", val:s.overridePass, color:"#fde68a", textColor:"#92400e" },
                        { label:"Missed issue", val:s.overrideFail, color:"#fecaca", textColor:"#dc2626" },
                      ].filter(x => x.val > 0).map(x => (
                        <span key={x.label} style={{ display:"flex", alignItems:"center", gap:3, fontSize:10, color:"var(--text-secondary)" }}>
                          <span style={{ width:7, height:7, borderRadius:2, background:x.color, flexShrink:0 }}/>
                          {x.label} ({x.val})
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* ── Worst checks ── */}
                  {s.worstChecks && s.worstChecks.length > 0 && (
                    <div style={{ borderTop:"1px solid var(--border)", paddingTop:10, marginTop:4 }}>
                      <div style={{ fontSize:11, fontWeight:600, color:"var(--text-primary)", marginBottom:8 }}>Checks AI struggles with most</div>
                      {s.worstChecks.map((wc, i) => {
                        const barColor = wc.overrideRate >= 40 ? "#dc2626" : wc.overrideRate >= 25 ? "#ea580c" : "#d97706";
                        return (
                          <div key={wc.checkId} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 0",
                            borderBottom: i < s.worstChecks.length - 1 ? "1px solid var(--border)" : "none" }}>
                            <span style={{ width:16, height:16, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center",
                              fontSize:9, fontWeight:500, flexShrink:0,
                              background: i < 3 ? (i === 0 ? "#fef2f2" : i === 1 ? "#fff7ed" : "#fffbeb") : "var(--gray-50, #f9fafb)",
                              color: i < 3 ? (i === 0 ? "#dc2626" : i === 1 ? "#c2410c" : "#b45309") : "var(--text-tertiary)" }}>
                              {i + 1}
                            </span>
                            <span style={{ flex:1, fontSize:11, color:"var(--text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                              {wc.description || wc.checkId}
                            </span>
                            <span style={{ fontSize:9, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", flexShrink:0 }}>{wc.checkId}</span>
                            <div style={{ width:60, flexShrink:0 }}>
                              <div style={{ height:3, background:"var(--gray-100, #f3f4f6)", borderRadius:2, overflow:"hidden" }}>
                                <div style={{ height:"100%", borderRadius:2, width:`${wc.overrideRate}%`, background:barColor }}/>
                              </div>
                            </div>
                            <span style={{ fontSize:10, fontFamily:"var(--font-mono)", fontWeight:500, color:barColor, width:30, textAlign:"right", flexShrink:0 }}>{wc.overrideRate}%</span>
                            <span style={{ fontSize:9, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", width:40, textAlign:"right", flexShrink:0 }}>{wc.overrides}/{wc.totalReviewed}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Recent Cases ────────────────────────────────── */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Recent Cases</div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate("/documents")}>View all →</button>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Case</th><th style={{ width:70 }}>Score</th><th style={{ width:90 }}>Status</th><th style={{ width:55 }}>Alerts</th><th style={{ width:130 }}>Uploaded</th><th style={{ width:110 }}>By</th>
              </tr></thead>
              <tbody>
                {recentDocs.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign:"center", color:"var(--text-tertiary)", padding:24, fontFamily:"var(--font-mono)", fontSize:12 }}>No cases yet — upload a document to get started</td></tr>
                ) : recentDocs.map((doc, i) => {
                  const hasAlert = doc.alertStatus === "ALERT" || doc.alertStatus === "MANUAL_ALERT";
                  return (
                    <tr key={i} onClick={() => navigate(`/documents/${encodeURIComponent(doc.id || doc.filename)}`)}>
                      <td className="td-filename">{doc.filename}</td>
                      <td>{doc.qaScore != null ? <ScoreRing score={doc.qaScore} size={36} strokeWidth={3}/> : <span className="td-mono">—</span>}</td>
                      <td><StatusBadge status={doc.status}/></td>
                      <td style={{ textAlign:"center" }}>
                        {hasAlert ? (
                          <span style={{ display:"inline-flex", alignItems:"center", gap:3, padding:"2px 6px", borderRadius:10,
                            background:"#fef2f2", border:"1px solid #fecaca", fontSize:10, fontWeight:600, color:"#dc2626", fontFamily:"var(--font-mono)" }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/></svg>{doc.alertCount || 1}
                          </span>
                        ) : <span style={{ color:"var(--text-tertiary)", fontSize:11 }}>—</span>}
                      </td>
                      <td className="td-mono">{doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}) : "—"}</td>
                      <td className="td-mono">{doc.uploadedBy || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}